import { useMemo, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { pickLang } from "@/data/stoicosCards";
import { findPhilosopher, ZENO_PAGE, CLEANTHES_PAGE, CHRYSIPPUS_PAGE, AL_THEMES, EVIDENCE_STATUS, SOURCE_LANGS } from "@/data/ancientLibrary";
import { entriesForPhilosopher } from "@/data/svfEntries";
import { MATERIAL_EVIDENCE } from "@/data/ancientImages";
import { AncientImageFigure } from "@/pages/ancient/AncientImageFigure";
import { AboutEditionNote, AlBreadcrumb, useAlLabels } from "@/pages/ancient/common";

export default function SvfPhilosopherPage() {
  const { philosopher } = useParams();
  const { lang, labels } = useAlLabels();
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const p = findPhilosopher(philosopher);

  useSEO({
    title: p ? `${p.name.en} — SVF Fragments & Testimonia | StoicOs Ancient Library` : "SVF | StoicOs.ai",
    description: p ? `${p.name.en} (${p.dates}): the surviving fragments and testimonia collected in Stoicorum Veterum Fragmenta, with Greek sources, StoicOs translations and commentary.` : "",
    canonical: p ? `https://stoicos.ai/ancient-library/svf/${p.slug}` : "https://stoicos.ai/ancient-library/svf",
    jsonLd: p ? [
      webPage({ name: `${p.name.en} — SVF`, description: `Fragments and testimonia of ${p.name.en}.`, url: `/ancient-library/svf/${p.slug}` }),
      breadcrumb([{ name: "Home", url: "/" }, { name: "Ancient Library", url: "/ancient-library" }, { name: "SVF", url: "/ancient-library/svf" }, { name: p.name.en, url: `/ancient-library/svf/${p.slug}` }]),
    ] : [],
  });

  const all = p && p.available ? entriesForPhilosopher(p.slug) : [];
  const haystacks = useMemo(() => {
    const map = {};
    for (const e of all) {
      map[e.slug] = [
        e.ref, e.svfNumber, e.witness.author, e.witness.work, e.witness.locus,
        e.ancientText || "", ...Object.values(e.translation || {}).filter(Boolean),
        ...(e.keywords || []),
      ].join(" \u0000 ").toLowerCase();
    }
    return map;
  }, [all]);

  if (!p || !p.available) return <Navigate to="/ancient-library/svf" replace />;

  const themesInUse = Object.keys(AL_THEMES).filter((t) => all.some((e) => (e.themes || []).includes(t)));
  const q = query.trim().toLowerCase();
  const toggleTheme = (t) => setSelected((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  const visible = all.filter((e) =>
    (selected.length === 0 || (e.themes || []).some((t) => selected.includes(t))) &&
    (q === "" || haystacks[e.slug].includes(q))
  );
  const filtersActive = q !== "" || selected.length > 0;

  return (
    <main data-testid="svf-philosopher-page" className="relative pt-32 md:pt-40 pb-32">
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <AlBreadcrumb items={[{ to: "/", label: "Home" }, { to: "/ancient-library", label: labels.library }, { to: "/ancient-library/svf", label: "SVF" }, { label: pickLang(p.name, lang) }]} />
        <p className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.volume} {p.svfVolume}</p>
        <h1 data-testid="philosopher-title" className="font-serif text-4xl sm:text-5xl text-bone tracking-tight leading-[1.08]">{pickLang(p.name, lang)}</h1>
        {p.greekName && <p className="mt-3 text-gold/90 text-2xl" style={{ fontFamily: '"EB Garamond", Georgia, "Times New Roman", serif' }}>{p.greekName} · <span className="text-fog/70 text-base tracking-[0.1em]">{p.dates}</span></p>}

        {p.slug === "zeno" && ZENO_PAGE.sections.map((s, i) => (
          <section key={i} className="mt-12">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{pickLang(s.h, lang)}</h2>
            <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(s.p, lang)}</p>
          </section>
        ))}
        {p.slug === "cleanthes" && CLEANTHES_PAGE.sections.map((s, i) => (
          <section key={i} className="mt-12">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{pickLang(s.h, lang)}</h2>
            <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(s.p, lang)}</p>
          </section>
        ))}
        {p.slug === "chrysippus" && CHRYSIPPUS_PAGE.sections.map((s, i) => (
          <section key={i} className="mt-12">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{pickLang(s.h, lang)}</h2>
            <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(s.p, lang)}</p>
          </section>
        ))}

        {/* Vita link */}
        {p.slug === "zeno" && (
          <Link
            to="/ancient-library/svf/zeno/vita"
            data-testid="zeno-vita-link"
            className="mt-14 block border border-gold/25 bg-gold/[0.04] px-6 py-6 hover:border-gold/50 transition-colors"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold">Vita · SVF I.1–46</p>
            <p className="mt-2 font-serif text-xl text-bone">
              {{ en: "The Life of Zeno — the ancient testimonia as one continuous account", nl: "Het leven van Zeno — de antieke getuigenissen als één doorlopend verhaal", de: "Das Leben Zenons — die antiken Zeugnisse als eine fortlaufende Erzählung", fr: "La vie de Zénon — les témoignages antiques en un récit continu", es: "La vida de Zenón — los testimonios antiguos como un relato continuo" }[lang]}
            </p>
          </Link>
        )}

        {p.slug === "cleanthes" && (
          <Link
            to="/ancient-library/svf/cleanthes/vita"
            data-testid="cleanthes-vita-link"
            className="mt-14 block border border-gold/25 bg-gold/[0.04] px-6 py-6 hover:border-gold/50 transition-colors"
          >
            <p className="text-[11px] tracking-[0.3em] uppercase text-gold">Vita · SVF I.463–481</p>
            <p className="mt-2 font-serif text-xl text-bone">
              {{ en: "The Life of Cleanthes — the ancient testimonia as one continuous account", nl: "Het leven van Cleanthes — de antieke getuigenissen als één doorlopend verhaal", de: "Das Leben des Kleanthes — die antiken Zeugnisse als eine fortlaufende Erzählung", fr: "La vie de Cléanthe — les témoignages antiques en un récit continu", es: "La vida de Cleantes — los testimonios antiguos como un relato continuo" }[lang]}
            </p>
          </Link>
        )}

        {/* Material Evidence: authentic objects & sites, provenance-carded */}
        {p.slug === "zeno" && (
          <section className="mt-16" data-testid="material-evidence-section">
            <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-4">{labels.materialEvidence}</h2>
            <p className="text-fog/75 font-reading text-sm md:text-base leading-[1.8] mb-8">{labels.materialEvidenceIntro}</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {MATERIAL_EVIDENCE.map((img) => (
                <AncientImageFigure key={img.id} img={img} lang={lang} labels={labels} portraitNarrow={!!img.portrait} />
              ))}
            </div>
          </section>
        )}

        {/* Entry index with thematic discovery */}
        <section className="mt-16" data-testid="svf-entry-index">
          <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-6">{labels.entries}</h2>
          <div className="mb-6">
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={labels.search}
              aria-label={labels.search}
              data-testid="svf-search-input"
              className="w-full bg-ink-elevated/40 border border-white/10 focus:border-gold/50 outline-none px-4 py-3 text-bone/90 font-reading text-base placeholder:text-fog/50 transition-colors"
            />
          </div>
          {themesInUse.length > 0 && (
            <div className="flex flex-wrap items-center gap-2.5 mb-4" role="group" aria-label={labels.themes}>
              <button type="button" aria-pressed={selected.length === 0} data-testid="theme-tab-all" onClick={() => setSelected([])}
                className={`px-3.5 py-1.5 text-[11px] tracking-[0.2em] uppercase border transition-colors ${selected.length === 0 ? "border-gold text-gold bg-gold/5" : "border-white/10 text-bone/70 hover:border-white/25"}`}>
                {labels.allThemes}
              </button>
              {themesInUse.map((th) => (
                <button key={th} type="button" aria-pressed={selected.includes(th)} data-testid={`theme-tab-${th}`} onClick={() => toggleTheme(th)}
                  className={`px-3.5 py-1.5 text-[11px] tracking-[0.2em] uppercase border transition-colors ${selected.includes(th) ? "border-gold text-gold bg-gold/5" : "border-white/10 text-bone/70 hover:border-white/25"}`}>
                  {pickLang(AL_THEMES[th], lang)}
                </button>
              ))}
            </div>
          )}
          <div className="flex items-baseline justify-between gap-4 mb-8">
            <p className="text-[11px] tracking-[0.22em] uppercase text-fog/60" data-testid="svf-results-count">{visible.length} / {all.length} {labels.results}</p>
            {filtersActive && (
              <button type="button" data-testid="svf-clear-filters" onClick={() => { setQuery(""); setSelected([]); }}
                className="text-[11px] tracking-[0.22em] uppercase text-gold/80 hover:text-gold transition-colors">
                {labels.clearFilters}
              </button>
            )}
          </div>
          {visible.length === 0 ? (
            <p className="text-fog/70 font-reading" data-testid="svf-no-entries">{filtersActive ? labels.noMatches : labels.noEntries}</p>
          ) : (
            <ul className="space-y-5">
              {visible.map((e) => (
                <li key={e.slug}>
                  <Link to={`/ancient-library/svf/${p.slug}/${e.slug}`} data-testid={`svf-entry-${e.slug}`} className="group block p-6 md:p-7 border border-white/10 bg-ink-elevated/40 hover:border-gold/40 transition-colors">
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                      <span className="font-serif text-xl text-gold">{e.ref}</span>
                      <span className="text-[11px] tracking-[0.18em] uppercase text-fog/70">{e.witness.author} · {pickLang(SOURCE_LANGS[e.witness.sourceLang], lang)}</span>
                      <span className="text-[11px] tracking-[0.18em] uppercase text-fog/50">{e.status.map((st) => pickLang(EVIDENCE_STATUS[st], lang)).join(" · ")}</span>
                    </div>
                    <p className="mt-3 text-bone/85 font-reading text-base leading-[1.75] line-clamp-2">{e.translation[lang] || e.translation.en}</p>
                    <p className="mt-3 text-[11px] tracking-[0.22em] uppercase text-gold/80 group-hover:text-gold transition-colors">{labels.open} →</p>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <AboutEditionNote />
      </div>
    </main>
  );
}
